import React, { useState, useEffect } from "react";
import { ContactContent } from "../../hooks/usePersistentContent";
import { useDraggablePosition } from "../../hooks/ui/useDraggablePosition";
import { formatWhatsAppNumber } from "../../utils/phoneUtils";

export interface FloatingWhatsAppProps {
  contact: ContactContent;
}

export const FloatingWhatsApp: React.FC<FloatingWhatsAppProps> = ({ contact }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosed, setIsClosed] = useState(false);
  const { position, isDragging, hasMoved, buttonRef, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggablePosition();

  useEffect(() => {
    if (!contact.phone) return;
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, [contact.phone]);

  // Reset shortcut
  useEffect(() => {
    const handleReset = (e: KeyboardEvent): void => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === "W") {
        e.preventDefault();
        setIsClosed(false);
        setTimeout(() => setIsVisible(true), 100);
      }
    };
    window.addEventListener("keydown", handleReset);
    return () => window.removeEventListener("keydown", handleReset);
  }, []);

  const handleClose = (e: React.MouseEvent): void => {
    e.stopPropagation();
    setIsClosed(true);
    setIsVisible(false);
    localStorage.removeItem("whatsapp_floating_position");
  };

  const handleWhatsAppClick = (): void => {
    if (isDragging || hasMoved || !contact.phone) return;
    const phoneNumber = formatWhatsAppNumber(contact.phone);
    if (!phoneNumber) return;
    window.open(`https://wa.me/${phoneNumber}`, "_blank", "noopener,noreferrer");
  };

  if (isClosed || !contact.phone || !isVisible) return null;

  return (
    <div
      ref={buttonRef}
      className="fixed z-[9999] md:hidden animate-fade-in touch-none select-none"
      style={{ left: `${position.x}px`, top: `${position.y}px`, transition: isDragging ? "none" : "all 0.3s ease-out", cursor: isDragging ? "grabbing" : "grab" }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute -top-1.5 -right-1.5 bg-white rounded-full p-1 shadow-xl active:bg-gray-100 transition-all duration-200 z-20 border-2 border-gray-200 hover:border-gray-300 hover:scale-110 active:scale-95"
          aria-label="Close WhatsApp button"
        >
          <svg className="w-3 h-3 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* WhatsApp button */}
        <button
          onClick={handleWhatsAppClick}
          className={`relative bg-gradient-to-br from-[#25D366] to-[#128C7E] active:from-[#20BA5A] active:to-[#0F7A6D] text-white rounded-full p-3 shadow-2xl transition-all duration-300 flex items-center justify-center group ${isDragging ? "scale-95" : "hover:scale-110 active:scale-105"}`}
          aria-label="Contact us on WhatsApp"
          style={{
            boxShadow: isDragging
              ? "0 15px 20px -5px rgba(37, 211, 102, 0.4), 0 8px 8px -5px rgba(37, 211, 102, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
              : "0 15px 30px -8px rgba(37, 211, 102, 0.6), 0 8px 15px -5px rgba(37, 211, 102, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1)",
          }}
        >
          <div className="absolute inset-0 rounded-full bg-white/30 animate-ping opacity-75" style={{ animationDuration: "1s" }} />
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#25D366]/40 to-[#128C7E]/40 blur-lg animate-pulse" />
          <div className="absolute inset-[1px] rounded-full bg-gradient-to-br from-white/20 to-transparent opacity-50" />
          <div className="relative z-10">
            <svg className="w-7 h-7 drop-shadow-lg transition-transform duration-300 group-hover:scale-110" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
};

export default FloatingWhatsApp;
